/**
 * Utility functions for reminder formatting
 */

/**
 * Formats days value for display, showing "1min" for test reminders (0.0007 days)
 * @param days - The number of days
 * @param showTest - Whether to show "(TEST)" suffix for test reminders
 * @param useAbbreviation - Whether to use "gg" instead of "giorni"
 * @returns Formatted string
 */
export const formatDaysForDisplay = (days: number, showTest: boolean = false, useAbbreviation: boolean = true): string => {
  if (days === 0.0007) {
    return showTest ? '1min (TEST)' : '1min';
  }
  return useAbbreviation ? `${days}gg` : `${days} giorni`;
};

/**
 * Formats an array of reminder days for display
 * @param daysArray - Array of reminder days
 * @param showTest - Whether to show "(TEST)" suffix for test reminders
 * @param useAbbreviation - Whether to use "gg" instead of "giorni"
 * @returns Formatted string with comma-separated values
 */
export const formatReminderDaysArray = (daysArray: number[], showTest: boolean = false, useAbbreviation: boolean = true): string => {
  return daysArray
    .sort((a, b) => b - a)
    .map(days => formatDaysForDisplay(days, showTest, useAbbreviation))
    .join(', ');
};

/**
 * Formats reminder days value (single or array) for display
 * @param value - Single number or array of reminder days
 * @param showTest - Whether to show "(TEST)" suffix for test reminders
 * @param useAbbreviation - Whether to use "gg" instead of "giorni"
 * @returns Formatted string
 */
export const formatReminderDisplay = (value: number | number[], showTest: boolean = false, useAbbreviation: boolean = true): string => {
  if (Array.isArray(value)) {
    return formatReminderDaysArray(value, showTest, useAbbreviation);
  }
  return formatDaysForDisplay(value, showTest, useAbbreviation);
};